/**
 * This is a re-usable component for rendering Assignnment data in an editable
 * way.  It is intended to be used for creation and edits
 **/
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useDispatch, useSelector} from "react-redux";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    updateClassDescriptors,
    updateEditedAssignment,
} from "@khan/assignments-store";
import {
    AssignmentStatus,
    type Assignment,
    type State,
    type Student,
    type UserHasChildAssignmentClassrooms,
} from "@khan/assignments-types";
import {
    getMinDate,
    getValidDueDate,
    isBeforeNow,
    validateDueDate,
    validateStartDate,
} from "@khan/assignments-utils";

import {AssignmentFormClassesAndStudents} from "./assignment-form-classes-and-students.tsx";
import DateTimeFieldset from "./date-time-fieldset.tsx";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

type AssignmentUpdateProps =
    | {
          startDate: Date | null | undefined;
      }
    | {
          dueDate: Date | null | undefined;
      }
    | {
          students: Array<Student>;
      };

// TODO(jangmi): Adjust Dropdown's dropdownBody border so it has same width
// button and dropdownBody.
const selectorWidth = 336;
// NOTE(alice): Dropdown component widths must account for our border width.
const dropdownHeadingWidth = selectorWidth - 2;
const dropdownBodyWidth = dropdownHeadingWidth - 2;

interface AssignmentFormProps {
    assignment: Assignment;
    maxDueDate?: Date;
    persistSettings: boolean;
    qualifiedContentDescriptorsList: Array<string>;
    storageKey?: string;
    userHasChildAssignmentsClassrooms: UserHasChildAssignmentClassrooms;
}

const AssignmentForm = (props: AssignmentFormProps) => {
    const {
        assignment,
        maxDueDate,
        persistSettings,
        qualifiedContentDescriptorsList,
        storageKey,
        userHasChildAssignmentsClassrooms,
    } = props;

    // Redux state
    const saving = useSelector((state: State) => state.saving);
    const selectedClassDescriptors = useSelector(
        (state: State) => state.editingAssignment.classDescriptors,
    );
    const studentLists = useSelector((state: State) => state.studentLists);
    const dispatch = useDispatch();

    // Local state
    const [hasChangedTime, setHasChangedTime] = React.useState(false);

    // Action dispatchers
    const onAssignmentUpdate = React.useCallback(
        (assignmentUpdate: AssignmentUpdateProps) => {
            dispatch(
                updateEditedAssignment(
                    assignmentUpdate,
                    persistSettings,
                    storageKey,
                ),
            );
        },
        [dispatch, persistSettings, storageKey],
    );

    const onClassDescriptorsUpdate = React.useCallback(
        (classDescriptors: Array<string>) => {
            dispatch(
                updateClassDescriptors(
                    classDescriptors,
                    persistSettings,
                    storageKey,
                ),
            );
        },
        [dispatch, persistSettings, storageKey],
    );

    // Handlers
    const handleClassesChange = React.useCallback(
        (classDescriptors: Array<string>) => {
            onClassDescriptorsUpdate(classDescriptors);
        },
        [onClassDescriptorsUpdate],
    );

    const handleStudentsChange = React.useCallback(
        (studentIds: Array<string>) => {
            const students: Array<Student> = studentIds.map((kaid) => ({kaid}));
            onAssignmentUpdate({students});
        },
        [onAssignmentUpdate],
    );

    const handleStartDateChanged = React.useCallback(
        (newDate: Date) => {
            const {startDate, dueDate} = assignment;

            let newStartDate: Date = new Date(newDate.getTime());

            // See if we changed the time.  If we haven't changed the
            // time, but we've changed the date, the we need to default
            // the time to 6 AM.  The effect we want to achieve is that
            // the time picker will show 6AM unless A, the teacher
            // otherwise changes it, or B, the teacher selects today
            // and it's after 6 AM.
            if (
                newStartDate.getHours() !== startDate.getHours() ||
                newStartDate.getMinutes() !== startDate.getMinutes()
            ) {
                setHasChangedTime(true);
            } else {
                if (!hasChangedTime) {
                    newStartDate.setHours(6, 0, 0);
                }
            }

            // The startDate will change to right now only if the user selects
            // "today" in the date picker. Otherwise, it will use the current value
            // set in the startDate field.
            newStartDate = isBeforeNow(newStartDate)
                ? new Date()
                : newStartDate;

            const newDueDate = getValidDueDate(
                newStartDate,
                dueDate,
                maxDueDate,
            );

            // Modify the assignment model only if the calculated dueDate is
            // different from the one passed in.
            if (dueDate !== newDueDate) {
                onAssignmentUpdate({
                    dueDate: newDueDate,
                    // TypeScript doesn't remember that newStartDate is still a Date,
                    // So we cast it here to be explicit to help TypeScript out.
                    startDate: newStartDate as Date,
                });

                return;
            }

            onAssignmentUpdate({startDate: newStartDate});
        },
        [assignment, hasChangedTime, maxDueDate, onAssignmentUpdate],
    );

    const handleDueDateChanged = React.useCallback(
        (newDate: Date) => {
            onAssignmentUpdate({dueDate: newDate});
        },
        [onAssignmentUpdate],
    );

    // Render helpers
    const renderStartDateFieldset = (): React.ReactNode => {
        const {assignmentStatus, startDate} = assignment;

        const isAssigned = assignmentStatus === AssignmentStatus.ASSIGNED;
        const dateWarningMessage = validateStartDate(assignment);

        return (
            <DateTimeFieldset
                width={dropdownBodyWidth}
                disabled={saving || isAssigned}
                currentDate={startDate}
                minDate={moment()}
                dateLabel={t`Start date`}
                timeLabel={t`Start time`}
                onDateChanged={handleStartDateChanged}
                dateLabelTooltip={
                    <Tooltip
                        content={t`The assignment will appear for your students on this date.`}
                    >
                        <PhosphorIcon icon={infoIcon} />
                    </Tooltip>
                }
                dateWarningMessage={dateWarningMessage}
                testId="start-date-fieldset"
                required={true}
            />
        );
    };

    const renderDueDateFieldset = (): React.ReactNode => {
        const {dueDate, startDate} = assignment;

        const minDate = getMinDate(startDate);
        const dateWarningMessage = validateDueDate(assignment, maxDueDate);

        return (
            <DateTimeFieldset
                width={dropdownBodyWidth}
                disabled={saving}
                currentDate={dueDate}
                minDate={minDate}
                maxDate={maxDueDate ? moment(maxDueDate) : undefined}
                dateLabel={t`Due date`}
                timeLabel={t`Due time`}
                onDateChanged={handleDueDateChanged}
                dateWarningMessage={dateWarningMessage}
                testId="due-date-fieldset"
                required={true}
            />
        );
    };

    return (
        <View>
            <AssignmentFormClassesAndStudents
                saving={saving}
                studentLists={studentLists}
                selectedClassDescriptors={selectedClassDescriptors}
                assignment={assignment}
                qualifiedContentDescriptorsList={
                    qualifiedContentDescriptorsList
                }
                dropdownBodyWidth={dropdownBodyWidth}
                userHasChildAssignmentsClassrooms={
                    userHasChildAssignmentsClassrooms
                }
                handleClassesChange={handleClassesChange}
                handleStudentsChange={handleStudentsChange}
            />

            <hr className={css(styles.divider)} aria-hidden="true" />

            {renderStartDateFieldset()}

            {renderDueDateFieldset()}
        </View>
    );
};

const styles = StyleSheet.create({
    divider: {
        boxShadow: "none",
        color: color.offBlack50,
        marginBlockStart: 18,
        marginBlockEnd: 18,
    },
});

export {AssignmentForm};
export default AssignmentForm;
